import { useEffect, useState } from 'react';

import { notify } from '../../app';
import {
  shiftDate,
  sortArray,
  getStoredSettings,
  saveSettings,
} from '../../utilities';
import { useFetch, useRequest, useSort } from '../../hooks';
import {
  getShortAgents,
  getWidgetAnalytics,
  getWidgetAnalyticsCountries,
} from '../../api';
import type { AnalyticsResponse } from '../../api';
import type { FilterValueProps } from '../../components/CountryFilterDropdown';

export type WidgetAnalyticsFormProps = {
  agentId: string;
  distinct: boolean;
  started_at?: string;
  ended_at?: string;
  countries?: FilterValueProps;
};

const STORAGE_KEY = 'widget_analytics_settings';

const defaultFilter = {
  agentId: '',
  distinct: true,
  countries: {
    selected: ['Ukraine'],
    mode: 'EXCLUDE' as const,
  },
};

export const useWidgetAnalytics = () => {
  const { sort, setSort } = useSort<string>();
  const storedSettings = getStoredSettings(STORAGE_KEY);

  const { loading: isAgentsLoading, data: agentsOptions } =
    useFetch(async () => {
      const data = await getShortAgents();

      return (data ?? []).map(d => ({
        value: d.id ?? '',
        label: d.title,
      }));
    }, []);

  const [formId, setFormId] = useState(1);
  const [form, setForm] = useState<WidgetAnalyticsFormProps | null>({
    ...(storedSettings?.filters ?? defaultFilter),
  });
  const [widgetAnalyticsData, setWidgetAnalyticsData] = useState<
    AnalyticsResponse[]
  >([]);

  const { loading: isCountriesLoading, data: countries } =
    useFetch(async () => {
      if (!form?.agentId || !form?.started_at || !form?.ended_at) return;

      const data = await getWidgetAnalyticsCountries(
        form?.agentId,
        form?.started_at,
        form?.ended_at
      );

      return data;
    }, [form?.agentId, form?.distinct, form?.started_at, form?.ended_at]);

  const [handleSubmitWidgetAnalytics, isSubmitting] = useRequest<
    AnalyticsResponse[]
  >(
    async _signal => {
      const {
        agentId,
        distinct,
        started_at = '',
        ended_at = '',
        countries,
      } = form || ({} as WidgetAnalyticsFormProps);

      const data = await getWidgetAnalytics({
        agentId,
        distinct,
        end: shiftDate(ended_at),
        start: shiftDate(started_at),
        countries: countries?.selected,
        mode: countries?.mode,
      });

      return data;
    },
    {
      onSuccess: data => {
        setWidgetAnalyticsData(data);
      },
      onError: () => {
        notify.error('Failed to get analytics data');
      },
      deps: [
        form?.agentId,
        form?.distinct,
        form?.started_at,
        form?.ended_at,
        form?.countries,
        form?.countries?.selected,
        form?.countries?.mode,
      ],
    }
  );

  useEffect(() => {
    if (form) {
      saveSettings(STORAGE_KEY, form, sort);
    }
  }, [form, sort]);

  return {
    form,
    formId: String(formId),
    onChange: setForm,
    setFormId,
    setForm,
    agentsOptions,
    billingData: sortArray(widgetAnalyticsData || [], sort),
    handleSubmitWidgetAnalytics,
    isSubmitting,
    isAgentsLoading,
    isCountriesLoading,
    countries,
    sort,
    onSort: setSort,
  };
};
